import React, { useState, useEffect, useCallback, useRef } from 'react'
import moment from 'moment'
import CalendarComponent from 'containers/scheduler/calendar'
import Button from 'components/controls/button'
import Modal from 'components/modal'
import TimezoneSelect from 'components/timezone-select'
import { timezone } from 'shared/helpers/user'
import API from 'store/scheduler/schedulerApis'
import 'less/project-availability.less'

/**
 * Project Timeslots — dashboard (project setup) panel.
 *
 * Shows the Scheduling Type toggle and, in "project" mode, a read-only LIST of the
 * project's availability windows plus an "Add / Manage Project Timeslots" button that
 * opens a modal hosting the REUSED moderator-availability calendar (drag to add,
 * click a window to remove — identical interactions). Windows can also be removed from
 * the list rows. Both removal paths show a booking-aware warning first.
 *
 * Safety: moderator mode is today's behavior (this panel simply persists the mode and
 * shows no calendar). Only the project_availability windows table is ever written; booked
 * interviews are never touched. Timezone handling mirrors the moderator flow exactly —
 * backend datetime strings are kept verbatim and re-sent unchanged; new windows are sent
 * as moment ISO; the calendar always renders from re-fetched backend data.
 */
const WINDOWS_PREVIEW_COUNT = 5

const toEvents = (rows, eventType) =>
  (rows || []).map((r) => ({
    id: r.id,
    startTime: r.startTime,
    endTime: r.endTime,
    eventType,
    // Render these tiles in the scheduler timezone (like moderator management), not the
    // logged-in user's timezone — event.js keys the tile-label timezone off this flag.
    isProjectAvailability: true,
    // Label window tiles as the Project Moderator (not the logged-in user, which the
    // calendar's event component otherwise falls back to for availability events).
    ...(eventType === 'availability' ? { firstName: 'Project', lastName: 'Moderator' } : {}),
  }))

const ProjectAvailability = ({
  projectId,
  user,
  userTimeZone,
  t,
  formatDate,
  showMessage,
  hideMessage,
  onSaved,
  onSchedulingTypeChange,
  onWindowsChange,
  updateTimeZone,
}) => {
  const safeUser = user && typeof user.userRole !== 'undefined' ? user : { userRole: 2, userId: 0 }
  const tz = userTimeZone || moment.tz.guess()

  const [loaded, setLoaded] = useState(false)
  const [schedulingType, setSchedulingType] = useState('moderator')
  // windows: { id, raw:{startTime,endTime}, startTime:Date, endTime:Date }
  const [windows, setWindows] = useState([])
  // interviews: { id, startTime:Date, endTime:Date }
  const [interviews, setInterviews] = useState([])
  const [saving, setSaving] = useState(false)
  const windowsRef = useRef([])
  const savingRef = useRef(false)
  const pendingWindowsRef = useRef([])
  const [modalOpen, setModalOpen] = useState(false)
  const [showAllWindows, setShowAllWindows] = useState(false)
  const [removeTarget, setRemoveTarget] = useState(null) // { id, count }
  // A 404 means the feature isn't enabled/deployed → render nothing (by design). Any other
  // failure is a real error: surface it and let the PM retry instead of hiding the panel forever.
  const [loadError, setLoadError] = useState(false)

  const loadData = useCallback(async () => {
    try {
      const res = await API.fetchProjectAvailability(projectId)
      if (res && res.status === 200 && res.data) {
        setLoadError(false)
        setLoaded(true)
        setSchedulingType(res.data.schedulingType || 'moderator')
        if (onSchedulingTypeChange) onSchedulingTypeChange(res.data.schedulingType || 'moderator')
        if (onWindowsChange) onWindowsChange(res.data.hasBookableSlot)
        const nextWindows = (res.data.windows || []).map((w) => ({
          id: w.id,
          raw: { startTime: w.startTime, endTime: w.endTime },
          startTime: new Date(w.startTime),
          endTime: new Date(w.endTime),
        }))
        windowsRef.current = nextWindows
        setWindows(nextWindows)
        if (nextWindows.length <= WINDOWS_PREVIEW_COUNT) setShowAllWindows(false)
        setInterviews(
          (res.data.interviews || []).map((i) => ({
            id: i.timeSlotId,
            startTime: new Date(i.startTime),
            endTime: new Date(i.endTime),
          }))
        )
      } else {
        // Resolved but not a usable payload — treat as unavailable and report moderator mode so
        // the dashboard's moderator-mode sections aren't left waiting on an answer.
        if (onSchedulingTypeChange) onSchedulingTypeChange('moderator')
      }
    } catch (e) {
      const status = e && e.response && e.response.status
      if (status === 404) {
        // Feature disabled / endpoint not deployed → render nothing, leave the dashboard unaffected.
        // Still report moderator mode so the dashboard's moderator-mode sections aren't left waiting.
        console.log('project availability unavailable (non-fatal):', e && e.message)
        if (onSchedulingTypeChange) onSchedulingTypeChange('moderator')
      } else {
        setLoadError(true)
        // Fall back to moderator mode (today's default) so a failure here can't disable the rest
        // of the dashboard's moderator-mode sections; the retry below re-reads the real value.
        if (onSchedulingTypeChange) onSchedulingTypeChange('moderator')
        if (showMessage) showMessage('Could not load project timeslots.')
        if (hideMessage) hideMessage()
      }
    }
  }, [projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData()
  }, [loadData])

  // A real load failure keeps the section visible with a retry, so a transient error can't
  // permanently hide Project Timeslots setup.
  if (!loaded && loadError) {
    return (
      <div className="project-availability">
        <div className="pa-empty">Could not load project timeslots.</div>
        <Button className="pa-add btn-primary" onClick={loadData}>RETRY</Button>
      </div>
    )
  }

  // Only appears once the backend confirms the feature is available for this project.
  if (!loaded) return null

  const persist = async (mode, windowsPayload) => {
    savingRef.current = true
    setSaving(true)
    let saved = false
    try {
      const res = await API.saveProjectAvailability(projectId, {
        schedulingType: mode,
        windows: windowsPayload,
      })
      if (res && res.status === 200) {
        saved = true
        await loadData()
        if (onSaved) onSaved()
      } else {
        throw new Error('save failed')
      }
    } catch (e) {
      if (showMessage) showMessage('Failed to save project timeslots.')
      if (hideMessage) hideMessage()
    } finally {
      savingRef.current = false
      setSaving(false)
    }
    const queued = pendingWindowsRef.current
    pendingWindowsRef.current = []
    if (saved && queued.length) {
      await persist('project', [...windowsRef.current.map((w) => w.raw), ...queued])
    }
  }

  const existingPayload = () => windowsRef.current.map((w) => w.raw)

  const changeMode = (mode) => {
    setSchedulingType(mode)
    if (onSchedulingTypeChange) onSchedulingTypeChange(mode)
    persist(mode, existingPayload())
  }

  const addWindow = ({ startTime, endTime }) => {
    const added = { startTime: startTime.toISOString(), endTime: endTime.toISOString() }
    if (savingRef.current) {
      pendingWindowsRef.current = [...pendingWindowsRef.current, added]
      return
    }
    persist('project', [...existingPayload(), added])
  }

  const countInterviewsInWindow = (start, end) => {
    const s = start.getTime()
    const e = end.getTime()
    return interviews.filter((i) => i.startTime.getTime() < e && i.endTime.getTime() > s).length
  }

  // From the calendar (click a window → event) or the list × (a window row). Both carry id/startTime/endTime.
  const requestRemove = (win) => {
    const count = countInterviewsInWindow(win.startTime, win.endTime)
    if (count > 0) {
      setRemoveTarget({ id: win.id, count })
    } else {
      doRemove(win.id)
    }
  }

  const doRemove = (id) => {
    setRemoveTarget(null)
    persist('project', windowsRef.current.filter((w) => String(w.id) !== String(id)).map((w) => w.raw))
  }

  // Render in the scheduler timezone (same source the moderator-management calendar uses),
  // English locale — so the list matches the calendar tiles exactly.
  const fmt = (d, f) => moment.tz(d, tz).locale('en').format(f)
  const fmtRange = (w) =>
    fmt(w.startTime, 'YYYY-MM-DD') === fmt(w.endTime, 'YYYY-MM-DD')
      ? `${fmt(w.startTime, 'ddd MMM DD')} · ${fmt(w.startTime, 'h:mm A')} – ${fmt(w.endTime, 'h:mm A')}`
      : `${fmt(w.startTime, 'ddd MMM DD')} ${fmt(w.startTime, 'h:mm A')} – ${fmt(w.endTime, 'ddd MMM DD')} ${fmt(w.endTime, 'h:mm A')}`

  return (
    <div className="project-availability">
      <div className="project-dashboard-sidebar-header">Scheduling type</div>
      <div className="line" />

      <label className={`pa-radio ${schedulingType === 'moderator' ? 'on' : ''}`}>
        <input type="radio" checked={schedulingType === 'moderator'} readOnly tabIndex={-1} onKeyDown={(e) => e.preventDefault()} onClick={() => { if (!saving) changeMode('moderator') }} />
        <span><b>Moderator timeslots</b></span>
      </label>
      <label className={`pa-radio ${schedulingType === 'project' ? 'on' : ''}`}>
        <input type="radio" checked={schedulingType === 'project'} readOnly tabIndex={-1} onKeyDown={(e) => e.preventDefault()} onClick={() => { if (!saving) changeMode('project') }} />
        <span><b>Project timeslots</b></span>
      </label>

      {schedulingType === 'project' ? (
        <div className="pa-list-wrap">
          <div className="project-dashboard-sidebar-header">Project timeslots</div>
          <div className="line" />
          {windows.length === 0 ? (
            <div className="pa-empty">No timeslots yet. Add windows for participants to book.</div>
          ) : (
            (showAllWindows ? windows : windows.slice(0, WINDOWS_PREVIEW_COUNT)).map((w) => (
              <div className="pa-row" key={w.id}>
                <svg className="pa-clock" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#2D90C0" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                <span className="pa-row-text">{fmtRange(w)}</span>
                <button
                  className="pa-remove"
                  disabled={saving}
                  title="Remove timeslot"
                  onClick={() => requestRemove(w)}
                >×</button>
              </div>
            ))
          )}
          {windows.length > WINDOWS_PREVIEW_COUNT ? (
            <button className="pa-toggle" onClick={() => setShowAllWindows(!showAllWindows)}>
              {showAllWindows ? 'Show less' : `Show all (${windows.length})`}
            </button>
          ) : null}
          <Button className="pa-add btn-primary" disabled={saving} onClick={() => setModalOpen(true)}>Add/Manage Project Timeslots</Button>
        </div>
      ) : null}

      {modalOpen ? (
        <Modal
          title="Project Timeslots"
          isOpen={true}
          onCancel={() => setModalOpen(false)}
          showSubmitBtn={false}
          showCancelBtn={false}
          className="project-availability-modal"
        >
          <div className="pa-modal-topbar">
            <div className="pa-modal-help">Drag to add a window · click a window to remove. Each time can be booked by one respondent.</div>
            {updateTimeZone ? (
              <div className="pa-modal-tz">
                <span className="pa-modal-tz-label">TIME ZONE (THIS CALENDAR ONLY)</span>
                <TimezoneSelect
                  searchable={false}
                  clearable={false}
                  value={tz}
                  placeholder={timezone.get(tz) || tz}
                  userTimeZone={tz}
                  onChange={(e) => { if (e && e.value) updateTimeZone(e.value) }}
                />
              </div>
            ) : null}
          </div>
          <div className="pa-modal-cal">
            <CalendarComponent
              key={`${projectId}-${tz}`}
              isProjectAvailability={true}
              availability={toEvents(windows, 'availability')}
              timeSlots={toEvents(interviews, 'timeSlot')}
              getSlots={loadData}
              selectSlot={addWindow}
              onRemoveWindow={requestRemove}
              allowAddAv={true}
              forAdmin={false}
              isModeratorManagement={false}
              selectedModerator={{ id: 0 }}
              moderatorColorsById={{}}
              userTimeZone={tz}
              moderatorManagementTimeZone={tz}
              user={safeUser}
              showMessage={showMessage}
              hideMessage={hideMessage}
              t={t}
              formatDate={formatDate}
            />
          </div>
        </Modal>
      ) : null}

      {removeTarget ? (
        <Modal
          title="Remove timeslot window"
          isOpen={true}
          onCancel={() => setRemoveTarget(null)}
          onSubmit={() => doRemove(removeTarget.id)}
          submitBtnText="Continue"
          cancelBtnText="Cancel"
          className="remove-window-modal"
        >
          <div style={{ padding: '4px 2px', lineHeight: 1.5 }}>
            <b>{removeTarget.count}</b> interview{removeTarget.count > 1 ? 's are' : ' is'} already
            booked in this window. Removing it won't cancel {removeTarget.count > 1 ? 'them' : 'it'} —
            it only stops new bookings. Continue?
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

export default ProjectAvailability
